/**
 * Remembers where a signed-out visitor was actually trying to go, so signing in
 * finishes the trip instead of dumping them on a default landing page.
 *
 * The move-in / move-out emails link tenants straight at
 * `/dashboard?tab=checkin` (see `lifecycle-emails.ts`). Those links are opened
 * on a phone days or weeks after the last visit, so the session is usually
 * gone: `ProtectedRoute` bounced them to the marketing homepage and every trace
 * of `?tab=checkin` was lost. After logging in they landed on the dashboard
 * Home tab and had to hunt for the Check-in/out section by hand — exactly the
 * problem the `?tab=` deep link was added to solve.
 *
 * The path is kept in `sessionStorage` rather than router state because the
 * login form lives on a different route (a modal on `/`), and a full page load
 * in between would drop anything held in memory. Being per-tab, the stash also
 * dies with the tab instead of following the visitor around.
 */

const STORAGE_KEY = "coliviq.auth.return-path";

/**
 * How long a stashed path stays usable. Long enough to cover "bounce → open the
 * login modal → reset a forgotten password → sign in", short enough that a tab
 * left open all afternoon doesn't teleport a later, unrelated login.
 */
const TTL_MS = 30 * 60 * 1000;

type StoredReturnPath = { p: string; t: number };

/**
 * Rejects anything that isn't a same-origin path.
 *
 * `sessionStorage` is writable by anything running on the origin, so the value
 * is re-validated on the way out and not just on the way in: `//evil.example`
 * and `/\evil.example` are both read as protocol-relative URLs by browsers and
 * would turn this into an open redirect.
 */
function isSafeInternalPath(path: string): boolean {
  if (!path.startsWith("/")) return false;
  if (path.startsWith("//")) return false;
  if (path.startsWith("/\\")) return false;
  return true;
}

/** Remember the protected location a signed-out visitor asked for. */
export function stashReturnPath(path: string): void {
  if (!isSafeInternalPath(path)) return;
  try {
    const payload: StoredReturnPath = { p: path, t: Date.now() };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* private mode / storage blocked — fall back to the default landing */
  }
}

/** Forget any stashed path (on sign-out, or once it has been used). */
export function clearReturnPath(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to clear if storage is unavailable */
  }
}

/**
 * Consume the stashed path, or `null` when there isn't a usable one.
 *
 * Always clears the stash, including when the path is rejected — a value that
 * can't be used now would only be more stale later.
 *
 * `canAccessAdmin` gates `/admin` destinations: a tenant who happens to have a
 * stale `/admin/...` stash must not be sent somewhere `ProtectedRoute` will
 * immediately bounce them out of again.
 */
export function takeReturnPath({
  canAccessAdmin,
}: {
  canAccessAdmin: boolean;
}): string | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  clearReturnPath();
  if (!raw) return null;

  let stored: StoredReturnPath;
  try {
    stored = JSON.parse(raw) as StoredReturnPath;
  } catch {
    return null;
  }

  if (typeof stored?.p !== "string" || typeof stored?.t !== "number") {
    return null;
  }
  if (!isSafeInternalPath(stored.p)) return null;
  if (Date.now() - stored.t > TTL_MS) return null;

  const isAdminPath =
    stored.p === "/admin" ||
    stored.p.startsWith("/admin?") ||
    stored.p.startsWith("/admin/") ||
    stored.p.startsWith("/admin#");
  if (isAdminPath && !canAccessAdmin) return null;

  return stored.p;
}
